import React, { useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  ArrowRight, Wifi, Package, Star, Router, HelpCircle,
  Globe, Headphones, ChevronRight, X
} from 'lucide-react';
import { slideDown } from '../animations';
import { trackClick, trackEvent } from '../../../utils/analytics';
import useComparisonStore from '../../../components/ComparisonModals/store/comparisonStore';

/**
 * PackagesMegaMenu - Polished mega menu with focus on network operators
 * and improved close functionality
 */
const PackagesMegaMenu = ({
  isOpen,
  onClose,
  openComparisonModal = null,
  openRecommendationModal = null,
  openWirelessAdvisorModal = null,
  openCoverageSidebar = null
}) => {
  const navigate = useNavigate();
  console.log('PackagesMegaMenu rendering with isOpen:', isOpen);
  
  // Get actions from the Zustand store
  const {
    openSelectionModal,
    openRecommendationModal: openRecommendationModalStore,
    closeAllModals
  } = useComparisonStore();
  
  // Reference to the menu container for click outside detection
  const menuRef = useRef(null);
  
  // Handle click outside to close menu
  useEffect(() => {
    if (!isOpen) return;
    
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose && onClose();
      }
    };
    
    // Track menu open event
    trackEvent('packages_menu_opened', { timestamp: new Date().toISOString() });
    
    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);
  
  if (!isOpen) {
    console.log('PackagesMegaMenu returning null because isOpen is false');
    return null;
  }
  
  const operators = [
    { id: 'frogfoot', name: 'Frogfoot', logo: 'https://apextech.group/frogfoot-bw.png' },
    { id: 'lightwire', name: 'Lightwire', logo: 'https://apextech.group/lightwire-colour.png' },
    { id: 'metro-fibre', name: 'Metro Fibre', logo: 'https://apextech.group/mfn-colour.png' },
    { id: 'octotel', name: 'Octotel', logo: 'https://apextech.group/octotel-colour.png' },
    { id: 'openserve', name: 'Openserve', logo: 'https://apextech.group/openserve-colour.png' },
    { id: 'seacom', name: 'Seacom', logo: 'https://apextech.group/seacom-bw.png' },
    { id: 'vumatel', name: 'Vumatel', logo: 'https://apextech.group/vuma-colour.png' },
    { id: 'open-fibre', name: 'Open Fibre', logo: 'https://apextech.group/OF_FullCol_white_landscape.png' }
  ];
  
  const handleOperatorClick = (operatorId) => {
    // Track the event (keep existing tracking)
    trackEvent('operator_selected', {
      operator_id: operatorId,
      operator_name: operators.find(op => op.id === operatorId)?.name
    });
    
    // Find the operator object
    const operator = operators.find(op => op.id === operatorId);
    
    if (operator) {
      // Get the operator name (not the ID)
      const operatorName = operator.name;
      
      console.log(`Operator selected: ${operatorName} (${operatorId})`);
      
      // Close the menu first
      onClose && onClose();
      
      console.log(`Opening selection modal with operator: ${operator.name}`);
      
      // Use the Zustand store to open the selection modal
      // Use a single function to handle modal opening to prevent multiple modals
      setTimeout(() => {
        // Use the functions from the hook instead of direct store access
        closeAllModals();
        
        // Then open the selection modal with the operator
        openSelectionModal({
          name: operator.name,
          id: operator.id,
          selectSingleOperator: true
        });
      }, 300);
    } else {
      // If operator not found, just close the menu
      onClose && onClose();
    }
  };
  
  return (
    <>
      {/* Transparent overlay to capture clicks outside the menu */}
      <div
        className="fixed inset-0 z-[48]"
        onClick={onClose}
      />
      
      <div
        className="fixed top-16 left-0 w-full z-[49]"
        style={{ animation: `${slideDown} 0.3s cubic-bezier(0.21, 1.02, 0.73, 1)` }}
        ref={menuRef}
      >
      {/* Solid background with subtle texture */}
      <div className="absolute inset-0 bg-gray-900 shadow-2xl"></div>
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI1MDAiIGhlaWdodD0iNTAwIj48ZmlsdGVyIGlkPSJhIiB4PSIwIiB5PSIwIj48ZmVUdXJidWxlbmNlIGJhc2VGcmVxdWVuY3k9Ii4wNSIgc3RpdGNoVGlsZXM9InN0aXRjaCIgdHlwZT0iZnJhY3RhbE5vaXNlIi8+PGZlQ29sb3JNYXRyaXggdHlwZT0ic2F0dXJhdGUiIHZhbHVlcz0iMCIvPjwvZmlsdGVyPjxwYXRoIGQ9Ik0wIDBoNTAwdjUwMEgweiIgZmlsdGVyPSJ1cmwoI2EpIiBvcGFjaXR5PSIuMDUiLz48L3N2Zz4=')] opacity-10 mix-blend-soft-light"></div>
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-purple-500/20 to-transparent"></div>
      
      <div className="relative max-w-7xl mx-auto px-10 py-8">
        {/* Header with close button */}
        <div className="flex items-center justify-between mb-7">
          <div className="flex items-center group">
            <div className="p-1.5 bg-purple-900/30 rounded-lg mr-3 group-hover:bg-purple-800/40 transition-colors duration-300">
              <Globe className="w-5 h-5 text-purple-400 group-hover:text-purple-300 transition-colors duration-300" />
            </div>
            <div>
              <h2 className="text-lg font-medium text-white group-hover:text-purple-50 transition-colors duration-300">Network Providers</h2>
              <p className="text-sm text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors duration-300">Select your local fibre provider</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-5">
            <Link
              to="/packages"
              className="text-sm text-purple-400 hover:text-purple-300 flex items-center transition-colors group"
              onClick={() => {
                trackClick('view_all_packages', 'menu_header');
                onClose && onClose();
              }}
            >
              <span className="relative">
                <span className="inline-block">View all packages</span>
                <span className="absolute bottom-0 left-0 w-0 group-hover:w-full h-px bg-purple-500/40 transition-all duration-300"></span>
              </span>
              <ChevronRight className="w-4 h-4 ml-1 transform group-hover:translate-x-0.5 transition-transform" />
            </Link>
            
            {/* Close button */}
            <button 
              onClick={onClose}
              className="p-1.5 rounded-full bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-gray-600 text-gray-400 hover:text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
              aria-label="Close menu"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        <div className="grid grid-cols-12 gap-8">
          {/* Network Operators - Enhanced with refined styling */}
          <div className="col-span-8">
            <div className="grid grid-cols-4 gap-4">
              {operators.map((operator) => (
                <div
                  key={operator.id}
                  onClick={() => handleOperatorClick(operator.id)}
                  className="group cursor-pointer"
                >
                  <div className="bg-gradient-to-b from-gray-800 to-gray-900 hover:from-gray-800/90 hover:to-gray-800 rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:shadow-purple-900/10 border border-gray-700/40 hover:border-purple-500/30 transition-all duration-300 h-full flex flex-col">
                    {/* Enhanced operator logo section */}
                    <div className="p-5 flex items-center justify-center h-28 relative overflow-hidden group-hover:bg-gradient-to-br group-hover:from-purple-900/20 group-hover:to-indigo-900/20 transition-all">
                      {/* Refined background accents */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        <div className="absolute top-0 left-0 right-0 h-0.5 bg-gradient-to-r from-transparent via-purple-500/50 to-transparent"></div>
                        <div className="absolute -bottom-1 -left-4 -right-4 h-8 bg-gradient-to-r from-purple-900/0 via-purple-900/20 to-purple-900/0 blur-md rounded-full"></div>
                      </div>
                      
                      {/* Enhanced subtle grid pattern */}
                      <div className="absolute inset-0 opacity-0 group-hover:opacity-20 transition-opacity duration-500 bg-[linear-gradient(rgba(255,255,255,0.05)_1px,transparent_1px),linear-gradient(90deg,rgba(255,255,255,0.05)_1px,transparent_1px)] bg-[size:20px_20px]"></div>
                      
                      {/* Polished logo treatment */}
                      <div className="relative z-10 transform transition-transform duration-300 group-hover:scale-110 group-hover:-rotate-1 w-full flex justify-center items-center">
                        <img
                          src={operator.logo}
                          alt={`${operator.name} logo`}
                          className="h-12 w-auto object-contain filter saturate-0 group-hover:saturate-100 brightness-90 group-hover:brightness-110 transition-all duration-300"
                        />
                      </div>
                    </div>
                    
                    {/* Refined separator */}
                    <div className="pt-0 pb-4 px-3 text-center relative">
                      <div className="h-0.5 w-12 mx-auto bg-gradient-to-r from-transparent via-purple-500/40 to-transparent mb-3 group-hover:w-20 transition-all duration-300"></div>
                      <h3 className="font-medium text-sm text-white group-hover:text-purple-100 transition-colors">{operator.name}</h3>
                    </div>
                    
                    {/* Enhanced action button */}
                    <div className="mt-auto px-3 pb-4">
                      <div className="px-3 py-1.5 bg-gray-800/80 group-hover:bg-gradient-to-r group-hover:from-purple-600 group-hover:to-purple-700 border border-gray-700/60 group-hover:border-purple-500/60 rounded-lg text-gray-400 group-hover:text-white text-xs font-medium flex items-center justify-center transition-all">
                        <span>View packages</span>
                        <ArrowRight className="w-3 h-3 ml-1.5 transition-transform group-hover:translate-x-1" />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Switch to Wondernet Card - Added beneath network providers */}
            <div className="mt-6">
              <Link
                to="/switch"
                onClick={() => {
                  trackClick('how_to_switch', 'packages_menu');
                  onClose && onClose();
                }}
                className="block bg-gradient-to-br from-gray-800 to-gray-900 hover:from-purple-900/20 hover:to-purple-800/20 rounded-xl overflow-hidden shadow-md hover:shadow-xl hover:shadow-purple-900/10 border border-gray-700/40 hover:border-purple-500/30 transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="p-5 flex items-center">
                  {/* Icon container */}
                  <div className="p-3 bg-purple-600/20 rounded-lg mr-4 group-hover:bg-purple-600/30 transition-all">
                    <ArrowRight className="w-5 h-5 text-purple-400" />
                  </div>
                  
                  {/* Content */}
                  <div>
                    <h3 className="font-medium text-white text-base mb-1">How to Switch to Wondernet</h3>
                    <p className="text-sm text-gray-400">Simple steps to join our network with no downtime</p>
                  </div>
                  
                  {/* Arrow indicator */}
                  <div className="ml-auto">
                    <div className="p-2 rounded-full bg-gray-800/80 group-hover:bg-purple-600/20 transition-all">
                      <ArrowRight className="w-4 h-4 text-purple-400" />
                    </div>
                  </div>
                </div>
              </Link>
            </div>
          </div>
          
          {/* Right Column - Enhanced promo and quick links */}
          <div className="col-span-4 space-y-5">
            {/* Enhanced Promo Card */}
            <div className="relative overflow-hidden rounded-xl group">
              {/* Enhanced gradient background with subtle pattern */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-700 via-purple-800 to-indigo-900"></div>
              <div className="absolute inset-0 opacity-10 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4xIj48cGF0aCBkPSJNMzYgMzR2Nmg2di02aC02em02IDZ2Nmg2di02aC02em0tMTIgMGg2djZoLTZ2LTZ6bTEyIDBoNnY2aC02di02eiIvPjwvZz48L2c+PC9zdmc+')]"></div>
              
              {/* Enhanced lighting effects */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 ease-out bg-[radial-gradient(circle_at_50%_50%,rgba(168,85,247,0.2)_0,transparent_60%)]"></div>
              <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-indigo-500/20 rounded-full blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
              
              {/* Enhanced content layout */}
              <div className="relative p-6 z-10">
                <div className="flex items-start">
                  <div className="mr-4 p-3 bg-white/10 backdrop-blur-md rounded-lg shadow-lg shadow-purple-900/20 transform group-hover:scale-105 transition-transform duration-300">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Uncapped Fibre</h3>
                    <p className="text-purple-100/90 text-sm mb-4 leading-relaxed">No throttling. No shaping. Just pure speed from R399/pm.</p>
                    <div className="flex flex-wrap gap-3">
                      <button
                        onClick={() => {
                          trackClick('view_all_plans', 'packages_menu');
                          onClose && onClose();
                          
                          // Open the comparison modal using the Zustand store
                          setTimeout(() => {
                            // First close any existing modals to prevent duplicates
                            closeAllModals();
                            
                            // Then open the selection modal with all operators
                            openSelectionModal({
                              selectAllOperators: true
                            });
                          }, 300);
                        }}
                        className="inline-flex items-center text-white text-sm font-medium transition-all bg-white/20 hover:bg-white/30 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 shadow-sm hover:shadow-lg hover:shadow-purple-900/10 group/btn"
                      >
                        <span>View Plans</span>
                        <ArrowRight className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                      
                      <button
                        onClick={() => {
                          trackClick('check_coverage', 'packages_menu');
                          onClose && onClose();
                          // Open the coverage sidebar if available
                          if (openCoverageSidebar && typeof openCoverageSidebar === 'function') {
                            setTimeout(() => {
                              openCoverageSidebar();
                            }, 300);
                          } else {
                            navigate('/coverage');
                          }
                        }}
                        className="inline-flex items-center text-white text-sm font-medium transition-all bg-white/10 hover:bg-white/20 backdrop-blur-sm px-4 py-2 rounded-lg border border-white/10 hover:border-white/20 shadow-sm hover:shadow-lg hover:shadow-purple-900/10 group/btn"
                      >
                        <span>Check Coverage</span>
                        <ArrowRight className="w-4 h-4 ml-2 transform group-hover/btn:translate-x-1 transition-transform" />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Enhanced Quick Links */}
            <div className="bg-gray-800 rounded-xl border border-gray-700/60 overflow-hidden shadow-lg">
              <div className="px-5 py-4 border-b border-gray-700/60 flex items-center">
                <HelpCircle className="w-4 h-4 text-purple-400 mr-2" />
                <h3 className="text-white font-medium text-sm">Need Help Choosing?</h3>
              </div>
              
              <div className="p-4 grid grid-cols-2 gap-3">
                {[
                  {
                    icon: Package,
                    title: "Compare",
                    description: "Side-by-side comparison",
                    link: "#",
                    onClick: () => {
                      trackClick('compare_packages', 'packages_menu');
                      onClose && onClose();
                      
                      // Open the comparison modal using the Zustand store
                      setTimeout(() => {
                        // First close any existing modals to prevent duplicates
                        closeAllModals();
                        
                        // Then open the selection modal with all operators
                        openSelectionModal({
                          selectAllOperators: true
                        });
                      }, 300);
                    }
                  },
                  {
                    icon: Star,
                    title: "Recommend",
                    description: "Find your perfect match",
                    link: "#",
                    onClick: () => {
                      trackClick('get_recommendation', 'packages_menu');
                      onClose && onClose();
                      
                      // Open the recommendation modal using the Zustand store
                      setTimeout(() => {
                        // First close any existing modals to prevent duplicates
                        const { closeAllModals, openRecommendationModal } = useComparisonStore.getState();
                        closeAllModals();
                        
                        // Then open the recommendation modal
                        openRecommendationModal();
                      }, 300);
                    },
                    highlight: true
                  },
                  {
                    icon: Router,
                    title: "WiFi Advisor",
                    description: "Smart home setup",
                    link: "#",
                    onClick: () => {
                      trackClick('wireless_advisor', 'packages_menu');
                      onClose && onClose();
                      // Open the wireless advisor modal if available
                      if (openWirelessAdvisorModal && typeof openWirelessAdvisorModal === 'function') {
                        setTimeout(() => {
                          openWirelessAdvisorModal();
                        }, 300);
                      } else {
                        navigate('/wireless-wonder');
                      }
                    }
                  },
                  {
                    icon: Headphones,
                    title: "Support",
                    description: "Talk to our experts",
                    link: "/support",
                    onClick: () => {
                      trackClick('support', 'packages_menu');
                      onClose && onClose();
                    }
                  }
                ].map((item, i) => (
                  <Link
                    key={i}
                    to={item.link}
                    onClick={(e) => {
                      e.preventDefault();
                      item.onClick && item.onClick();
                    }}
                    className={`flex flex-col p-3 rounded-lg transition-all duration-200 group ${
                      item.highlight 
                        ? 'bg-gradient-to-br from-purple-600/20 to-purple-700/20 hover:from-purple-600/30 hover:to-purple-700/30 border border-purple-500/30 shadow-sm hover:shadow hover:shadow-purple-900/10' 
                        : 'hover:bg-gray-700 border border-transparent hover:border-gray-600/60 hover:shadow hover:shadow-gray-900/5'
                    }`}
                  >
                    <div className={`self-start p-2 rounded-lg mb-2 ${
                      item.highlight 
                        ? 'bg-gradient-to-br from-purple-600 to-purple-700 text-white shadow-md shadow-purple-900/20' 
                        : 'bg-gray-700 text-purple-400 group-hover:bg-gradient-to-br group-hover:from-purple-600 group-hover:to-purple-700 group-hover:text-white group-hover:shadow-md group-hover:shadow-purple-900/10'
                      } transition-all`}>
                      <item.icon className="w-4 h-4" />
                    </div>
                    <div className="font-medium text-white text-xs">{item.title}</div>
                    <div className="text-xs text-gray-400 mt-0.5 group-hover:text-gray-300 transition-colors">{item.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    </>
  );
};

export default PackagesMegaMenu;